import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Subscription } from 'rxjs';

import { PublicacionesPlataformaService } from '../../services/publicaciones-plataforma.service';
import { PublicacionErpDTO } from '../../services/workspace/DTO/publicacion-plataforma.dto';
import { NovedadesModalComponent } from './novedades-modal.component';

const LIMITE_HISTORIAL = 10;

@Component({
  selector: 'app-novedades-plataforma',
  templateUrl: './novedades-plataforma.component.html',
  styleUrls: ['./novedades-plataforma.component.scss'],
})
export class NovedadesPlataformaComponent implements OnInit, OnDestroy {
  contador = 0;
  pendientes: PublicacionErpDTO[] = [];
  historial: PublicacionErpDTO[] = [];
  paginaHistorial = 0;
  totalPaginasHistorial = 0;
  cargandoHistorial = false;
  errorHistorial = '';
  abriendo: string | null = null;

  private modalAbierto = false;
  private bloqueado = false;
  private omitidasSesion = new Set<string>();
  private subscription: Subscription;

  constructor(
    private publicacionesService: PublicacionesPlataformaService,
    private modalService: NgbModal
  ) {}

  /**
   * Sigue los pendientes del usuario y el bloqueo de mantenimiento; cada vez
   * que llegan novedades nuevas intenta mostrar el popup.
   */
  ngOnInit(): void {
    this.subscription = combineLatest([
      this.publicacionesService.pendientes$,
      this.publicacionesService.activo$,
    ]).subscribe(([pendientes, activo]) => {
      this.pendientes = pendientes?.items || [];
      this.contador = pendientes?.total || 0;
      this.bloqueado = !!activo;
      this.mostrarPendientes();
    });
  }

  /** Libera la observacion al salir del layout autenticado. */
  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  /** Identidad por uuid y revision: una edicion publicada vuelve a mostrarse. */
  private clave(publicacion: PublicacionErpDTO): string {
    return this.publicacionesService.claveAviso(publicacion.uuid, publicacion.revision);
  }

  /**
   * Abre el popup con los pendientes que no se cerraron en esta sesion. No
   * interrumpe otro modal ni el bloqueo de mantenimiento; el siguiente sondeo
   * lo vuelve a intentar.
   */
  private mostrarPendientes(): void {
    if (this.modalAbierto || this.bloqueado || this.modalService.hasOpenModals()) return;
    const nuevas = this.pendientes.filter(p => !this.omitidasSesion.has(this.clave(p)));
    if (nuevas.length) this.abrirModal(nuevas, true);
  }

  /** Reabre todos los pendientes desde el historial, aunque se hayan cerrado antes. */
  verPendientes(): void {
    if (this.pendientes.length && !this.modalAbierto) this.abrirModal(this.pendientes, true);
  }

  /**
   * Muestra las publicaciones en el modal paginado. Al cerrarlo, por
   * cualquier via, no se vuelven a abrir solas en esta sesion; solo con
   * "Entendido" se marcan vistas en el servidor (las canceladas no). Devuelve
   * si el usuario termino de leerlas.
   */
  async abrirModal(publicaciones: PublicacionErpDTO[], marcar: boolean): Promise<boolean> {
    this.modalAbierto = true;
    const ref = this.modalService.open(NovedadesModalComponent, {
      centered: true,
      backdrop: 'static',
      backdropClass: 'novedades-plataforma-backdrop',
      windowClass: `erp-tab-modal novedades-plataforma-modal${publicaciones.length > 1 ? ' con-nav' : ''}`,
    });
    ref.componentInstance.publicaciones = publicaciones;
    let entendido = false;
    try {
      await ref.result;
      entendido = true;
    } catch (motivo) {
      entendido = false;
    }
    publicaciones.forEach(p => this.omitidasSesion.add(this.clave(p)));
    this.modalAbierto = false;
    if (entendido && marcar) {
      const vistas = publicaciones
        .filter(p => !p.cancelada)
        .map(p => ({ uuid: p.uuid, revision: p.revision }));
      if (await this.publicacionesService.marcarVistas(vistas)) {
        const marcadas = new Set(vistas.map(v => v.uuid));
        this.historial.forEach(item => {
          if (marcadas.has(item.uuid)) item.vista = true;
        });
      }
    }
    return entendido;
  }

  /** Al abrir el panel se carga la primera pagina del historial. */
  alCambiarPanel(abierto: boolean): void {
    if (abierto) this.cargarHistorial(1);
  }

  /**
   * Carga una pagina del historial; la primera reemplaza la lista y las
   * siguientes se agregan al final ("Ver mas").
   */
  async cargarHistorial(page: number): Promise<void> {
    this.cargandoHistorial = true;
    this.errorHistorial = '';
    try {
      const resultado = await this.publicacionesService.listarHistorial(page, LIMITE_HISTORIAL);
      this.historial = page === 1 ? resultado.items : [...this.historial, ...resultado.items];
      this.paginaHistorial = resultado.paginacion.page;
      this.totalPaginasHistorial = resultado.paginacion.totalPages;
    } catch (error) {
      this.errorHistorial = 'No se pudo cargar el historial de novedades.';
    } finally {
      this.cargandoHistorial = false;
    }
  }

  /**
   * Abre una publicacion del historial con su detalle completo y cierra el
   * panel cuando el detalle llego; se marca vista al terminar solo si estaba
   * pendiente y sigue vigente.
   */
  async abrirDesdeHistorial(item: PublicacionErpDTO, panel?: { close(): void }): Promise<void> {
    if (this.abriendo || this.modalAbierto) return;
    this.abriendo = item.uuid;
    let detalle: PublicacionErpDTO;
    try {
      detalle = await this.publicacionesService.obtenerDetalle(item.uuid);
    } catch (error) {
      this.errorHistorial = 'No se pudo abrir la novedad.';
      return;
    } finally {
      this.abriendo = null;
    }
    if (panel) panel.close();
    await this.abrirModal([detalle], !detalle.vista && !detalle.cancelada);
  }

  /** Fecha corta en hora de Ecuador. */
  formatearFecha(fecha: string): string {
    if (!fecha) return '';
    return new Intl.DateTimeFormat('es-EC', {
      timeZone: 'America/Guayaquil',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(fecha));
  }
}
